import IconButton from 'common/components/IconButton';
import { TableNavButton } from 'common/styles/TableNavButton';
import React, { useEffect, useState } from 'react';
import { ReactComponent as GridIcon } from 'common/icons/four-grid.svg';
import { useSearchParams } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Line, LineChart, RadialBar, RadialBarChart, Rectangle, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { SmallLabelCard, ProductivityContainer, RiskContainer, DashboardSmallContainer } from '../styles/DashboardStyles';
import toFixedIfNecessary from 'common/utils/toFixedIfNecessary';
import numberWithSpaces from 'common/utils/numberWithSpaces';
import UserDataTableProps from 'common/types/UserDataTableProps';
import { Avatar, CircularProgress } from '@mui/material';
import { stringAvatar } from 'common/utils/stringToColor';
import { useAuth } from 'common/context/AuthContext';
import sendFetch from 'common/utils/fetch';
import { ReactComponent as FilledStarIcon } from 'common/icons/rating-star-filled.svg';
import SearchInput from 'common/components/SearchInput';
import { ReactComponent as FilterIcon } from 'common/icons/filter.svg';
import { ReactComponent as SortIcon } from 'common/icons/sort.svg';
import { ClientLineWrapper, ClientLineLeft, ClientsTopWrapper, ClientsTopContainer, ClientsListWrapper, ClientsList, ClientsInfoWrapper, ClientsInfoContainer, ClientInfoSmallBox, ClientsBottomWrapper, ClientsBottomContainer, OrdersListWrapper, OrdersList, OrderLine, OrdersInfoWrapper, OrderDestinationLine, OrderReadyLine } from '../styles/DashboardInfoStyles';
import CustomTooltip from '../components/CustomTooltip';

const DashboardSmallLabelCard = ({ value }: { value: number }) => {

  return (
        <SmallLabelCard negative={value < 0}>
            <span className='dashboard-small-label-text'>{(value >= 0 ? '+' : '-') + toFixedIfNecessary(Math.abs(value), 2) + '%'}</span>
        </SmallLabelCard>
  );
};

const ClientLine = ({ name, rating, avatar, selected, onClick }: { name: string, rating: string | number, avatar: string | React.ReactNode, selected: boolean, onClick: () => void }) => {
  return (
        <ClientLineWrapper onClick={onClick} className={selected ? 'active' : ''}>
            <ClientLineLeft>
                {avatar}
                <p className='new-p'>{name}</p>
            </ClientLineLeft>
            <div className='dashboard-rating-wrapper'>
                <span className='dashboard-rating-text'>{rating}</span>
                <FilledStarIcon />
            </div>
        </ClientLineWrapper>
  );
};

const productivityData = [
  {
    name: 'Понеділок',
    total: 7400,
    gain: 2400,
    loss: 1600,
  },
  {
    name: 'Вівторок',
    total: 5000,
    gain: 1398,
    loss: 1602,
  },
  {
    name: 'Середа',
    total: 0,
    gain: 9800,
    loss: 7800,
  },
  {
    name: 'Четвер',
    total: 280,
    gain: 3908,
    loss: 1128,
  },
  {
    name: 'П\'ятниця',
    total: 890,
    gain: 4800,
    loss: 2910,
  },
  {
    name: 'Субота',
    total: 4390,
    gain: 3800,
    loss: 1410,
  },
  {
    name: 'Неділя',
    total: 6490,
    gain: 4300,
    loss: 810,
  },
];

const riskData = [
  {
    name: null,
    uv: 100,
    fill: 'transparent',
  },
  {
    name: 'Підтверджений',
    uv: 40,
    fill: 'rgba(249, 11, 182, 0.25)',
  },
  {
    name: 'У виробництві',
    uv: 20,
    fill: '#900BF940',
  },
  {
    name: 'Виданий',
    uv: 80,
    fill: '#1E0BF940',
  },
  {
    name: 'Скасовано',
    uv: 50,
    fill: '#0B78F940',
  },
  {
    name: 'У чернетці',
    uv: 30,
    fill: '#0BEBF940',
  },
];

const orderData = [
  {
    name: 'Page A',
    uv: 4000,
    pv: 2400,
    amt: 2400,
  },
  {
    name: 'Page B',
    uv: 3000,
    pv: 1398,
    amt: 2210,
  },
  {
    name: 'Page C',
    uv: 2000,
    pv: 9800,
    amt: 2290,
  },
  {
    name: 'Page D',
    uv: 2780,
    pv: 3908,
    amt: 2000,
  },
  {
    name: 'Page E',
    uv: 1890,
    pv: 4800,
    amt: 2181,
  },
  {
    name: 'Page F',
    uv: 2390,
    pv: 3800,
    amt: 2500,
  },
  {
    name: 'Page G',
    uv: 3490,
    pv: 4300,
    amt: 2100,
  },
  {
    name: 'Page H',
    uv: 2490,
    pv: 6300,
    amt: 1100,
  },
];

const clientCountData = [
  {
    name: 'Page A',
    uv: 4000,
    pv: 2400,
    amt: 2400,
  },
  {
    name: 'Page B',
    uv: 3000,
    pv: 1398,
    amt: 2210,
  },
  {
    name: 'Page C',
    uv: 2000,
    pv: 9800,
    amt: 2290,
  },
  {
    name: 'Page D',
    uv: 2780,
    pv: 3908,
    amt: 2000,
  },
  {
    name: 'Page E',
    uv: 1890,
    pv: 4800,
    amt: 2181,
  },
  {
    name: 'Page F',
    uv: 2390,
    pv: 3800,
    amt: 2500,
  },
  {
    name: 'Page G',
    uv: 3490,
    pv: 4300,
    amt: 2100,
  },
  {
    name: 'Page H',
    uv: 2490,
    pv: 6300,
    amt: 1100,
  },
];

type ClientProps = UserDataTableProps & {
  rating: number | string,
};

const servicesData = [
  15145154,
  31335345,
  13513513,
  88866532,
  31321367,
  36757756,
  87654321,
];

export default function DashboardHeadOfDepartment() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { companyId } = useAuth();
  const [clientList, setClientList] = useState<ClientProps[]>([]);
  const [selectedClient, setSelectedClient] = useState<number | string>(null);
  const type = searchParams.get('type') || 'overview';
  const productivityDataTotal = productivityData.reduce((acc, item) => acc + item.total, 0);
  const [clientListLoading, setClientListLoading] = useState(false);
  const [orderSearchValue, setOrderSearchValue] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<number>(null);

  const handleSearchChange = (value: string) => {
    setOrderSearchValue(value);
  };

  const handleClientClick = (id: number | string) => {
    setSelectedClient(id);
  };

  useEffect(() => {
    const fetchTableData = async () => {
      setClientListLoading(true);
      const formData = new FormData();
      formData.append('companyId', companyId);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response: [number, any] = await sendFetch('https://api-workspace.astin.net/api/user/get-client-list', {
        method: 'POST',
        data: formData,
      });
      console.log(response);
      if (response[0] === 200) {
        setClientListLoading(false);
        const newList = response[1].map((item: ClientProps) => {
          const randomRating = Math.random() * (5 - 1) + 1;
          return {
            ...item,
            rating: toFixedIfNecessary(randomRating, 1),
          };
        });
        setClientList(newList);
      } else if (response[1] = 'Empty list') {
        setClientListLoading(false);
      }
    };
    if (companyId) {
      fetchTableData();
    }
  }, [type, companyId]);

  return (
        <>
            <div className='dashboard-nav-wrapper'>
                <TableNavButton className={type === 'overview' ? 'active' : ''} onClick={() => setSearchParams({ type: 'overview' })}>Загальне</TableNavButton>
                <TableNavButton className={type === 'clients' ? 'active' : ''} onClick={() => setSearchParams({ type: 'clients' })}>Клієнти</TableNavButton>
            </div>
            {type !== 'clients' ? <div className='dashboard-content-wrapper'>
                <div className='dashboard-overview-top-wrapper'>
                    <ProductivityContainer>
                        <div className='dashboard-productivity-top-line'>
                            <span className='big-text'>Виробництво</span>
                            <div className='dashboard-productivity-button-wrapper'>
                                <IconButton size={36}>
                                    <GridIcon className='svg-black' />
                                </IconButton>
                            </div>
                        </div>
                        <div className='dashboard-productivity-revenue-wrapper'>
                            <h2 className='new-h2'>{numberWithSpaces(productivityDataTotal)} ₴</h2>
                            <span className='dashboard-big-text'>Загальна виручка</span>
                        </div>
                        <div className='dashboard-graph-wrapper'>
                            <div className='dashboard-graph-absolute-wrapper'>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        width={500}
                                        height={300}
                                        data={productivityData}
                                        margin={{
                                          top: 0,
                                          right: 0,
                                          left: 0,
                                          bottom: 0,
                                        }}
                                        barSize={56}

                                    >
                                        <XAxis strokeDasharray="3 3" dataKey="name" scale="point" padding={{ left: 50, right: 50 }} />
                                        <YAxis tickLine={false} axisLine={false} />
                                        <CartesianGrid vertical={false} strokeDasharray="3 3" />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Bar activeBar={<Rectangle fill="rgba(30, 11, 249, 1)" />} radius={[8, 8, 0, 0]} dataKey={'total'} fill="rgba(30, 11, 249, 0.50)" background={{ fill: 'rgba(155, 158, 171, 0.15)', radius: '8px 8px 0 0' }} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </ProductivityContainer>
                    <RiskContainer>
                        <span className='big-text'>Показник ризиків</span>
                        <span style={{ marginTop: '4px' }} className='dashboard-big-text'>Замовлення</span>
                        <div style={{ maxHeight: '500px' }} className='dashboard-graph-wrapper'>
                            <div className='dashboard-graph-absolute-wrapper'>
                                <ResponsiveContainer width="100%" height="100%">
                                    <RadialBarChart startAngle={360 + 90} endAngle={90} innerRadius="15%" outerRadius="100%" barSize={15} data={riskData}>
                                        <RadialBar
                                            label={false}
                                            background={false}
                                            dataKey="uv"
                                            maxBarSize={100}
                                            cornerRadius={15}
                                        />
                                    </RadialBarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                        <div className='dashboard-legend-wrapper'>
                            {riskData.map((item, index) => {
                              if (item.name === null) return null;
                              return (
                                    <div key={index} className='dashboard-legend-line'>
                                        <div className='dashboard-legend-line-left'>
                                            <div style={{ backgroundColor: item.fill }} className='dashboard-legend-color'></div>
                                            <span className='dashboard-legend-text'>{item.name}</span>
                                        </div>
                                        <span className='dashboard-legend-text'>{item.uv}</span>
                                    </div>
                              );
                            })}
                        </div>
                    </RiskContainer>
                </div>
                <div className='dashboard-overview-bottom-wrapper'>
                    <DashboardSmallContainer>
                        <div className='dashboard-overview-bottom-absolute-wrapper'>
                            <div className='dashboard-small-top-line'>
                                <div className='dashboard-small-title-line'>
                                    <h2 className='new-h2'>{numberWithSpaces(268)}</h2>
                                    <DashboardSmallLabelCard value={2.75} />
                                </div>
                                <span className='dashboard-big-text'>Кількість клієнтів</span>
                            </div>
                            <div className='dashboard-graph-wrapper'>
                                <div className='dashboard-graph-absolute-wrapper'>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart
                                            width={300}
                                            height={300}
                                            data={productivityData}
                                            margin={{
                                              top: 0,
                                              right: 0,
                                              left: 0,
                                              bottom: 0,
                                            }}
                                            barSize={22}

                                        >
                                            <Bar radius={[8, 8, 0, 0]} dataKey="loss" fill="#0BEBF940" background={{ fill: 'transparent' }} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                    </DashboardSmallContainer>
                    <DashboardSmallContainer>
                        <div className='dashboard-overview-bottom-absolute-wrapper'>
                            <div className='dashboard-small-top-line'>
                                <div className='dashboard-small-title-line'>
                                    <h2 className='new-h2'>{numberWithSpaces(23)}</h2>
                                    <DashboardSmallLabelCard value={-0.83} />
                                </div>
                                <span className='dashboard-big-text'>Речі на пунктах видачі</span>
                            </div>
                            <div className='dashboard-graph-wrapper'>
                                <div className='dashboard-graph-absolute-wrapper'>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart width={300} height={100} data={orderData}>
                                            <Line type="monotone" dot={{ fill: '#F90BB666', height: 4, r: 2, width: 4 }} dataKey="pv" stroke="#F90BB666" strokeWidth={2} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                    </DashboardSmallContainer>
                    <DashboardSmallContainer>
                        <div className='dashboard-overview-bottom-absolute-wrapper'>
                            <div className='dashboard-small-top-line'>
                                <div className='dashboard-small-title-line'>
                                    <h2 className='new-h2'>{numberWithSpaces(15)}</h2>
                                    <DashboardSmallLabelCard value={10.51} />
                                </div>
                                <span className='dashboard-big-text'>Кількість виданих речей</span>
                            </div>
                            <div className='dashboard-graph-wrapper'>
                                <div className='dashboard-graph-absolute-wrapper'>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart width={300} height={100} data={clientCountData}>
                                            <Line type="monotone" dot={{ fill: '#900BF966', height: 4, r: 2, width: 4 }} dataKey="uv" stroke="#900BF966" strokeWidth={2} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                    </DashboardSmallContainer>
                    <DashboardSmallContainer>
                        <div className='dashboard-overview-bottom-absolute-wrapper'>
                            <div className='dashboard-small-top-line'>
                                <div className='dashboard-small-title-line'>
                                    <h2 className='new-h2'>+{numberWithSpaces(1.25)}%</h2>
                                    <DashboardSmallLabelCard value={-6.24} />
                                </div>
                                <span className='dashboard-big-text'>Відсоток браку</span>
                            </div>
                            <div className='dashboard-graph-wrapper'>
                                <div className='dashboard-graph-absolute-wrapper'>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart
                                            width={300}
                                            height={300}
                                            data={orderData}
                                            margin={{
                                              top: 0,
                                              right: 0,
                                              left: 0,
                                              bottom: 0,
                                            }}
                                            barSize={22}

                                        >
                                            <Bar radius={[8, 8, 0, 0]} dataKey="pv" fill="#0B78F966" background={{ fill: 'transparent' }} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                    </DashboardSmallContainer>
                </div>
            </div> :
                <div className='dashboard-content-wrapper'>
                    <ClientsTopWrapper>
                        <ClientsTopContainer>
                            <ClientsListWrapper>
                                <ClientsList className='u-fancy-scrollbar'>
                                    {!clientListLoading ? clientList.map((item, index) => {
                                      return (
                                            <ClientLine
                                                key={index}
                                                name={item.companyName ? item.companyName : `${item.lastName} ${item.firstName} ${item.patronymic}`}
                                                avatar={<Avatar {...stringAvatar(item.companyName ? item.companyName : `${item.lastName} ${item.firstName}`)} />}
                                                selected={selectedClient === item.id}
                                                rating={item.rating}
                                                onClick={() => handleClientClick(item.id)}
                                            />
                                      );
                                    }) :
                                        <div className='dashboard-loading-wrapper'>
                                            <CircularProgress sx={{
                                              color: theme => theme.colors.primaryColor,
                                            }} />
                                        </div>
                                    }
                                </ClientsList>
                            </ClientsListWrapper>
                            <ClientsInfoWrapper>
                                <ClientsInfoContainer>
                                    <div className='dashboard-client-info-top-line'>
                                        <span className='big-text'>Інформація по клієнту</span>
                                        <IconButton>
                                            <GridIcon className='svg-black' />
                                        </IconButton>
                                    </div>
                                    <div className='dashboard-client-info-graph-wrapper'>
                                        <div className='dashboard-client-info-graph-container'>
                                            <div className='dashboard-client-info-graph'>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart
                                                        width={500}
                                                        height={300}
                                                        data={productivityData}
                                                        margin={{
                                                          top: 0,
                                                          right: 0,
                                                          left: 0,
                                                          bottom: 0,
                                                        }}
                                                        barSize={56}

                                                    >
                                                        <XAxis strokeDasharray="3 3" dataKey="name" scale="point" padding={{ left: 50, right: 50 }} />
                                                        <YAxis tickLine={false} axisLine={false} />
                                                        <Tooltip cursor={{ fill: 'rgba(30, 11, 249, 1)' }} />
                                                        <CartesianGrid vertical={false} strokeDasharray="3 3" />
                                                        <Bar activeBar={<Rectangle fill="rgba(249, 11, 182, 0.40)" />} radius={[8, 8, 0, 0]} dataKey={'total'} fill="rgba(249, 11, 182, 0.40)" background={{ fill: 'rgba(155, 158, 171, 0.15)', radius: '8px 8px 0 0' }} />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                        <div className='dashboard-client-info-graph-info'>
                                            <div className='dashboard-client-info-graph-line'>
                                                <span className='dashboard-client-info-graph-big-title'>{numberWithSpaces(26)}</span>
                                                <span className='dashboard-rating-text'>Кількість заказів</span>
                                            </div>
                                            <div className='dashboard-client-info-graph-line'>
                                                <span className='dashboard-client-info-graph-big-title'>{numberWithSpaces(80)}%</span>
                                                <span className='dashboard-rating-text'>Ефективність обслуговування</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='dashboard-client-info-box-wrapper'>
                                        <ClientInfoSmallBox>
                                            <span className='dashboard-client-info-title'>
                                                {numberWithSpaces(15000)} ₴
                                            </span>
                                            <span className='dashboard-rating-text'>
                                                Прибуток
                                            </span>
                                        </ClientInfoSmallBox>
                                        <ClientInfoSmallBox>
                                            <span className='dashboard-client-info-title'>
                                                {numberWithSpaces(10)}
                                            </span>
                                            <span className='dashboard-rating-text'>
                                                Підтверджено
                                            </span>
                                        </ClientInfoSmallBox>
                                        <ClientInfoSmallBox>
                                            <span className='dashboard-client-info-title'>
                                                {numberWithSpaces(10)}
                                            </span>
                                            <span className='dashboard-rating-text'>
                                                Видано
                                            </span>
                                        </ClientInfoSmallBox>
                                        <ClientInfoSmallBox>
                                            <span className='dashboard-client-info-title'>
                                                {numberWithSpaces(6)}
                                            </span>
                                            <span className='dashboard-rating-text'>
                                                Скасовано
                                            </span>
                                        </ClientInfoSmallBox>
                                    </div>
                                </ClientsInfoContainer>
                            </ClientsInfoWrapper>
                        </ClientsTopContainer>
                    </ClientsTopWrapper>
                    <ClientsBottomWrapper>
                        <ClientsBottomContainer>
                            <OrdersListWrapper>
                                <div className='dashboard-client-order-search-wrapper'>
                                    <SearchInput wrapperStyle={{ maxWidth: 'unset', width: 'unset', flex: 1 }} style={{ maxWidth: 'unset' }} value={orderSearchValue} onChange={handleSearchChange} placeholder='Пошук...' />
                                    <IconButton>
                                        <FilterIcon className='svg-black' />
                                    </IconButton>
                                    <IconButton>
                                        <SortIcon className='svg-black' />
                                    </IconButton>
                                </div>
                                <div className='dashboard-client-order-list-wrapper'>
                                    <OrdersList className='u-fancy-scrollbar'>
                                        {servicesData.map((item, index) => {
                                          return (
                                                <OrderLine
                                                    key={index}
                                                    className={selectedOrder === item ? 'active' : ''}
                                                    onClick={() => setSelectedOrder(item)}
                                                >
                                                    {'№' + item}
                                                </OrderLine>
                                          );
                                        })}
                                    </OrdersList>
                                </div>
                            </OrdersListWrapper>
                            <OrdersInfoWrapper>
                                <div style={{ marginBottom: '16px' }} className='dashboard-client-info-top-line'>
                                    <span className='big-text'>Інформація по клієнту</span>
                                    <div style={{ height: '40px' }}></div>
                                </div>
                                <span className='dashboard-client-order-title'>№ {selectedOrder}</span>
                                <div className='dashboard-client-order-destinations-wrapper'>
                                    <div className='dashboard-client-order-destination'>
                                        <span className='dashboard-big-text gray-text'>15 лист. 2023 20:08</span>
                                        <span className='dashboard-big-text gray-text'>Одеса</span>
                                    </div>
                                    <OrderDestinationLine />
                                    <div className='dashboard-client-order-destination right'>
                                        <span className='dashboard-big-text gray-text'>17 лист. 2023 16:35</span>
                                        <span className='dashboard-big-text gray-text'>Одеса</span>
                                    </div>
                                </div>
                                <OrderReadyLine>
                                    <span className='dashboard-client-order-title'>
                                        Замовлення виконано
                                    </span>
                                </OrderReadyLine>
                                <div className='dashboard-client-order-info-wrapper'>
                                    <div className='dashboard-client-order-info-side'>
                                        <div className='dashboard-client-order-info-line'>
                                            <p className='new-p'>Категорія:</p>
                                            <p className='new-p'>Прання</p>
                                        </div>
                                        <div className='dashboard-client-order-info-line'>
                                            <p className='new-p'>Вид одягу:</p>
                                            <p className='new-p'>Сорочка простого фасону</p>
                                        </div>
                                    </div>
                                    <div className='dashboard-client-order-info-side'>
                                        <div className='dashboard-client-order-info-line'>
                                            <p className='new-p'>Замовник:</p>
                                            <p className='new-p'>Матіяш Йосипа Олександрівна</p>
                                        </div>
                                        <div className='dashboard-client-order-info-line'>
                                            <p className='new-p'>Ціна:</p>
                                            <p className='new-p'>5 000 ₴</p>
                                        </div>
                                    </div>
                                </div>
                            </OrdersInfoWrapper>
                        </ClientsBottomContainer>
                    </ClientsBottomWrapper>
                </div>
            }
        </>
  );
}